import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { removeFirstFromArray } from "@cervval.js/common/src/tools/collection.tools";
import { assertIsNever } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { ACTION_TYPE, Action, ActionPersistData, ElementToUpdate, Operation, OperationType } from "../action.model";
import { getOrCreateElementToUpdate } from "./opUpdate";

export type AddReferenceValue = { addReference: { id: number } };
export type AddValue = { addValue: { value: unknown } };
export type AddObject = { addObject: { dto: unknown } };
export type AddToArrayValue = AddReferenceValue | AddValue | AddObject;

export class OpAddToArray<ElementTypes, TypeName extends keyof ElementTypes, ElementType extends ElementTypes[TypeName], AppLogInfos, Context> implements Operation<ElementTypes, AppLogInfos, Context> {

    // TODO comment
    constructor(protected readonly _elementTypeName: TypeName,
        protected _elementToUpdate: ElementType,
        protected readonly _field: keyof ElementTypes[TypeName],
        protected _objectToAdd: AddToArrayValue,
        protected _index?: number) {
    }

    /**
     * Get _field
     */
    public getField(): keyof ElementTypes[TypeName] {
        return this._field;
    }

    /**
     * Get _objectToAdd
     */
    public getObjectToAdd(): AddToArrayValue {
        return this._objectToAdd;
    }

    /** @inheritdoc */
    public getElement(): ElementType {
        return this._elementToUpdate;
    }

    /** @inheritdoc */
    public get elementTypeName(): keyof ElementTypes { return this._elementTypeName; }

    /**
     * @returns the object to add to the array based on this._objectToAdd
     */
    protected _getObjectToAdd(): unknown {
        if ("addReference" in this._objectToAdd) {
            return this._objectToAdd.addReference.id;
        }
        if ("addValue" in this._objectToAdd) {
            return this._objectToAdd.addValue.value;
        }
        if ("addObject" in this._objectToAdd) {
            return this._objectToAdd.addObject.dto;
        }
        throw new Error("unexpected _objectToAdd " + this._objectToAdd);
    }

    /**
     * Add element to array
     */
    public process(action: Action<ElementTypes, AppLogInfos, Context>): void {
        let list = this._elementToUpdate[this._field] as unknown as unknown[] | undefined;
        if (list == null) {
            // if the array is not defined, we create it
            list = [];
            this._elementToUpdate[this._field] = list as anyReallyAny;// need to force any here...
        }
        if (!Array.isArray(list)) {
            throw new Error(`${String(this._elementTypeName)}.${String(this._field)} is not an array for ${this._elementToUpdate}`);
        }
        const objectToAdd = this._getObjectToAdd();
        if (this._index != null && this._index >= 0 && this._index < list.length) {
            list.splice(this._index, 0, objectToAdd);
        } else {
            list.push(objectToAdd);
            this._index = list.length - 1;
        }

    }

    /**
     * Gets the id of the referenced object (when using { addObject: { dto: unknown } })
     */
    protected _getReferencedObjectId(object: anyReallyAny): number | undefined {
        const idProperty = "id";
        return object ? object[idProperty] : undefined;
    }

    /**
     * Remove element from array
     */
    public undo(action: Action<ElementTypes, AppLogInfos, Context>): void {
        const list = this._elementToUpdate[this._field] as unknown as unknown[] | undefined;
        if (list == null) {
            // do not throw error in undo process, simply log error
            injector.find<LogService>(LOG_SERVICE)?.error(`${String(this._elementTypeName)}.${String(this._field)} is not an array for`, this._elementToUpdate);
            return;
        }
        const objectToAdd = this._getObjectToAdd();
        if (this._index != null && this._index >= 0 && this._index < list.length && list[this._index] === objectToAdd) {
            // we found the element at the expected index
            list.splice(this._index, 1);
        } else {
            // need to search for the element in the array

            if ("addObject" in this._objectToAdd && this._getReferencedObjectId(objectToAdd)) {
                const objectToAddId = this._getReferencedObjectId(objectToAdd);
                // find the referenced object
                this._index = undefined;
                for (let i = 0; i < list.length; i++) {
                    const dto = list[i];
                    if (this._getReferencedObjectId(dto) === objectToAddId) {
                        this._index = i;
                        break;
                    }
                }
            } else {
                // find the value
                this._index = list.indexOf(objectToAdd);
                if (this._index < 0) {
                    this._index = undefined;
                }
            }

            // remove found element
            if (this._index != null && this._index >= 0) {
                list.splice(this._index, 1);
            }
        }
    }

    /**
     * Returns the type of the current Operation
     * @returns {OperationType}
     */
    public getOperationType(): OperationType {
        return OperationType.ADD;
    }

    /**
     * Action to string
     * @param action 
     * @returns {string}
     */
    public toString(action: Action<ElementTypes, AppLogInfos, Context>): string {
        return `Add ${this._getObjectToAdd()} to ${String(this._elementTypeName)}.${String(this._field)}`;
    }

    /**
     * update internal objects (retrieve the element from the cache or update ids and foreign keys)
     */
    public actualize(action: Action<ElementTypes, AppLogInfos, Context>): void {
        const cache = action.getCacheManager();
        const cacheRepository = cache.getDTOCacheRepository(this._elementTypeName);
        this._elementToUpdate = cacheRepository.getUpToDateElement(this._elementToUpdate) as ElementType;

        if ("addReference" in this._objectToAdd) {
            const foreignTable = cache.getForeignKeyTableName(this._elementTypeName, this._field);
            if (foreignTable == null) {
                getLogConsole().error("Couldn't find table name in table definition. Please ensure your foreign table definition is correctly implemented", this.elementTypeName, this._field);
            } else {
                const refCacheRepository = action.getCacheManager().getDTOCacheRepository(foreignTable);
                const oldReferenceId = this._objectToAdd.addReference.id;
                this._objectToAdd.addReference.id = refCacheRepository.getUpToDateId(oldReferenceId);

                // if id was updated, update the id in the dto's list
                // The DTO is also updated so next operations will have the correct id, because actualize is not called before process.
                if (oldReferenceId !== this._objectToAdd.addReference.id) {
                    const list = this._elementToUpdate[this._field] as unknown[] | undefined;
                    if (list?.length) {
                        for (let i = 0; i < list.length; i++) {
                            if (list[i] === oldReferenceId) {
                                // we replace old id by the up to date id
                                list[i] = this._objectToAdd.addReference.id;
                            }
                        }
                    }
                }
            }
        } else if ("addObject" in this._objectToAdd) {
            // TODO
        } else if ("addValue" in this._objectToAdd) {
            // nothing to do
        } else {
            assertIsNever(this._objectToAdd);
        }
    }

    /**
     * Add server server action element to ActionPersistenceData
     */
    public addServerActionElement(actionPersistenceData: ActionPersistData<Context>, params: { elementsToUpdateByTypeAndId: Record<string, ElementToUpdate>, action: Action<ElementTypes, AppLogInfos, Context>, actionType: ACTION_TYPE }): void {
        return this._addServerActionElement(actionPersistenceData, {
            action: params.action, actionType: params.actionType, elementsToUpdateByTypeAndId: params.elementsToUpdateByTypeAndId,
            operation: params.actionType === ACTION_TYPE.UNDO ? "remove" : "add"
        });
    }

    /**
     * Add server server action element to ActionPersistenceData for add or remove operation
     */
    protected _addServerActionElement(actionPersistenceData: ActionPersistData<Context>, params: { elementsToUpdateByTypeAndId: Record<string, ElementToUpdate>, operation: "add" | "remove", action: Action<ElementTypes, AppLogInfos, Context>, actionType: ACTION_TYPE }): void {
        const cacheRepository = params.action.getCacheManager().getDTOCacheRepository(this._elementTypeName);
        const elementId = cacheRepository.getId(this._elementToUpdate);
        const elementsToUpdate = getOrCreateElementToUpdate(actionPersistenceData, this._elementTypeName as string, elementId, params.elementsToUpdateByTypeAndId);
        const fieldStr = this._field as string;
        const objectToAdd: anyReallyAny = this._getObjectToAdd();

        // register value to add
        if (params.operation === "remove") {
            // Check if value is added in the same action, don't add it
            if (!removeFirstFromArray(elementsToUpdate.valuesToAdd?.[fieldStr] ?? [], objectToAdd)) {// TODO might not work for addObject

                if (!elementsToUpdate.valuesToRemove) {
                    elementsToUpdate.valuesToRemove = {};
                }
                if (!elementsToUpdate.valuesToRemove[fieldStr]) {
                    elementsToUpdate.valuesToRemove[fieldStr] = [];
                }

                elementsToUpdate.valuesToRemove[fieldStr].push(objectToAdd);
            }
            // Check if value is removed in the same action, don't remove it
        } else if (!removeFirstFromArray(elementsToUpdate.valuesToRemove?.[fieldStr] ?? [], objectToAdd)) {// TODO might not work for addObject
            if (!elementsToUpdate.valuesToAdd) {
                elementsToUpdate.valuesToAdd = {};
            }
            if (!elementsToUpdate.valuesToAdd[fieldStr]) {
                elementsToUpdate.valuesToAdd[fieldStr] = [];
            }
            elementsToUpdate.valuesToAdd[fieldStr].push(objectToAdd);
        }
    }
}